import { AlertTriangle, Flame, Hourglass, ShieldCheck } from 'lucide-react';

import type { RoiDataset } from '../types/roi';
import { formatCompactCurrency, formatCurrency } from '../utils/format';
import { RoiCumulativeChart } from './RoiCumulativeChart';

interface RoiHeroSectionProps {
  dataset: RoiDataset;
  /** Full-coverage hero figure - what would be captured with every integration on. */
  fullCoverageHeroUsd: number;
}

export function RoiHeroSection({
  dataset,
  fullCoverageHeroUsd,
}: RoiHeroSectionProps) {
  const recallPct = Math.round(
    (dataset.caughtProjects / dataset.totalProjects) * 100
  );
  const { late, firefight } = dataset.buckets;
  return (
    <section className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-[0_1px_0_rgba(15,23,42,0.04)]">
      <div className="grid grid-cols-1 gap-0 lg:grid-cols-12">
        <div className="border-b border-gray-100 p-5 lg:col-span-5 lg:border-b-0 lg:border-e lg:border-gray-100">
          <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
            Recoverable rework cost ·{' '}
            {dataset.assumptions.timeWindowLabel.toLowerCase()}
          </span>
          <h1 className="mt-2 text-[44px] font-bold leading-[0.95] tracking-tighter tabular-nums text-red-600 sm:text-5xl lg:text-[56px]">
            {formatCurrency(dataset.heroTotalUsd)}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-500">
            <span className="tabular-nums">
              of{' '}
              <span className="font-semibold text-slate-700">
                {formatCompactCurrency(fullCoverageHeroUsd)}
              </span>{' '}
              at full coverage
            </span>
            <span className="h-1 w-1 rounded-full bg-slate-300" />
            <span className="tabular-nums">
              {recallPct}% recall · {dataset.caughtProjects} of{' '}
              {dataset.totalProjects} projects
            </span>
          </div>
          <p className="mt-2.5 text-[11px] leading-relaxed text-slate-500">
            <span className="font-semibold text-slate-700">
              Engineering rework only.
            </span>{' '}
            Customer churn, missed revenue, and bug-fix cost not included.
          </p>
        </div>
        <div className="p-5 lg:col-span-7">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400">
              Captured month by month
            </span>
            <span className="text-[10px] font-medium text-slate-400">
              {dataset.monthlyCumulative[0]?.monthLabel} -{' '}
              {
                dataset.monthlyCumulative[dataset.monthlyCumulative.length - 1]
                  ?.monthLabel
              }
            </span>
          </div>
          <RoiCumulativeChart points={dataset.monthlyCumulative} />
        </div>
      </div>

      <div className="grid grid-cols-2 border-t border-gray-100 sm:grid-cols-4">
        <KpiTile
          icon={Hourglass}
          label="Engineer time stuck on rework"
          value={formatEngineerDays(dataset)}
          sub={`across ${dataset.caughtProjects} projects`}
        />
        <KpiTile
          icon={AlertTriangle}
          label="Total weeks past deadline"
          value={`${late.totalWeeksSlipped} wk`}
          sub={`worst: ${late.worstSlipWeeks} wk slip`}
        />
        <KpiTile
          icon={Flame}
          label="Post-launch firefights"
          value={`${firefight.hotfixPrCount}`}
          sub={`hotfix PRs · ${firefight.escalationMeetingsCount} escalations`}
        />
        <KpiTile
          icon={ShieldCheck}
          label="Intera recall"
          value={`${recallPct}%`}
          sub={`${dataset.caughtProjects} / ${dataset.totalProjects} caught`}
          positive
        />
      </div>
    </section>
  );
}

function formatEngineerDays(dataset: RoiDataset): string {
  const totalReworkDays = dataset.projects.reduce(
    (sum, project) => sum + project.reworkEngineerDays,
    0
  );
  return `${totalReworkDays.toLocaleString('en-US')} d`;
}

interface KpiTileProps {
  icon: typeof Hourglass;
  label: string;
  value: string;
  sub: string;
  positive?: boolean;
}

function KpiTile({ icon: Icon, label, value, sub, positive }: KpiTileProps) {
  const valueColor = positive ? 'text-emerald-600' : 'text-slate-900';
  const iconColor = positive ? 'text-emerald-500/70' : 'text-slate-400';
  return (
    <div className="relative flex flex-col gap-1 border-e border-gray-100 px-5 py-3 last:border-e-0">
      <div className="flex items-center gap-1.5">
        <Icon className={`h-3 w-3 ${iconColor}`} />
        <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500">
          {label}
        </span>
      </div>
      <div
        className={`text-2xl font-bold leading-none tracking-tight tabular-nums ${valueColor}`}
      >
        {value}
      </div>
      <span className="text-[10px] tabular-nums text-slate-400">{sub}</span>
    </div>
  );
}
